import { ONE_SECOND } from '@air/utilities';

/**
 * This key holds an encoded URI representing the redirect location after a log out is
 * finished. The value is an initialism representing "redirect path and query string".
 */
export const redirectURIKey = 'rpaqs';

/**
 * Same-origin post-verify resume URL (e.g. skip-onboarding `/home?…`).
 * Stored in localStorage so an email verification link opened in a new tab
 * can still resume. Invite join uses `JOIN_WORKSPACE_INFO_KEY`.
 */
export const AUTH_RESUME_REDIRECT_KEY = 'auth-resume-redirect';

/** Pending invite payload for post-verify join. Stored in localStorage; consumption requires a matching boundEmail. */
export const JOIN_WORKSPACE_INFO_KEY = 'join-workspace-info';

/** Must stay longer than `/verify` auto-continue (`REDIRECT_TO_WORKSPACE_DELAY_MS`). */
const AUTH_RESUME_STATE_CLEAR_DELAY_SECONDS = 15;

export const AUTH_RESUME_STATE_CLEAR_DELAY_MS = AUTH_RESUME_STATE_CLEAR_DELAY_SECONDS * ONE_SECOND;

let scheduledAuthResumeClearId: number | undefined;

export const getSharedAuthStorageItem = (key: string): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.localStorage.getItem(key);
};

export const setSharedAuthStorageItem = (key: string, value: string): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(key, value);
};

export const removeSharedAuthStorageItem = (key: string): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.removeItem(key);
};

export const clearSharedAuthResumeState = (): void => {
  if (typeof window !== 'undefined' && scheduledAuthResumeClearId !== undefined) {
    window.clearTimeout(scheduledAuthResumeClearId);
    scheduledAuthResumeClearId = undefined;
  }

  removeSharedAuthStorageItem(AUTH_RESUME_REDIRECT_KEY);
  removeSharedAuthStorageItem(JOIN_WORKSPACE_INFO_KEY);
};

export const scheduleClearSharedAuthResumeState = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const hasResumeRedirect = window.localStorage.getItem(AUTH_RESUME_REDIRECT_KEY) !== null;
  const hasJoinWorkspaceInfo = window.localStorage.getItem(JOIN_WORKSPACE_INFO_KEY) !== null;

  if (!hasResumeRedirect && !hasJoinWorkspaceInfo) {
    return;
  }

  if (scheduledAuthResumeClearId !== undefined) {
    window.clearTimeout(scheduledAuthResumeClearId);
  }

  scheduledAuthResumeClearId = window.setTimeout(() => {
    scheduledAuthResumeClearId = undefined;
    removeSharedAuthStorageItem(AUTH_RESUME_REDIRECT_KEY);
    removeSharedAuthStorageItem(JOIN_WORKSPACE_INFO_KEY);
  }, AUTH_RESUME_STATE_CLEAR_DELAY_MS);
};

/** We want to know if a password-protected short ID has been verified this session to prevent prefetches. */
export const getKeyForShortIDVerificationStatus = (shortID: string) => `${shortID}-sidvs`;

/** Session storage key to check if saml has been enforced in an
 * enterprise workspace that an account belongs to
 */
export const SAML_SSO_ENFORCED = 'SAML SSO enforced';

/**
 * Sentinel flag set by the confirm-email page just before redirecting to
 * `/login`, so the login page can fire a "new email verified" toast on
 * arrival. Does NOT store the email address — the frontend can't derive
 * it from the verify token, and the user may even land here from a
 * logged-out session, so we keep the message generic.
 */
export const EMAIL_CHANGE_VERIFIED_KEY = 'emailChangeVerified';
